import React from 'react';
import LegalDoc from '../../components/LegalDoc';

export default function BackupRunbook() {
  return (
    <LegalDoc
      slug="backup-runbook"
      title="MongoDB Backup &amp; Recovery Runbook"
      subtitle="Snapshot frequency, retention, restoration drill schedule, RTO &amp; RPO targets."
      version="1.0"
      lastReviewed="2026-02-18"
      controls={["CC7.1"]}
    >
      <h2>Backup strategy</h2>
      <p>CARTIE runs on MongoDB Atlas. Atlas Cloud Backup provides continuous snapshot-based protection at the cluster level. We layer per-tenant export jobs on top for auditability.</p>

      <h3>Tier 1: Atlas Cloud Backup (cluster-level)</h3>
      <ul>
        <li><strong>Snapshot cadence</strong>: continuous (every 6 hours full snapshot + WiredTiger oplog continuous replay)</li>
        <li><strong>Retention</strong>: 7 daily · 4 weekly · 12 monthly = ~13 month rolling window</li>
        <li><strong>Cross-region copy</strong>: enabled (us-east-1 → us-west-2)</li>
        <li><strong>Encryption</strong>: AES-256 server-side at rest in S3; AWS KMS-managed keys</li>
        <li><strong>Point-in-time recovery</strong>: down to 1-second granularity within last 7 days</li>
      </ul>

      <h3>Tier 2: Per-tenant export (auditability)</h3>
      <p>A weekly cron (Sundays 03:00 UTC, runs immediately after Pipeline Dedup cron) iterates every tenant DB and writes a Cloud-Backup snapshot reference into <code>backup_audit_log</code>. This gives operators a "tenant X exists in snapshot Y" attestation without taking redundant data copies.</p>

      <h2>RTO &amp; RPO targets</h2>
      <table>
        <thead><tr><th>Scenario</th><th>RPO target</th><th>RTO target</th><th>Achievement</th></tr></thead>
        <tbody>
          <tr><td>Single-document corruption (operator deletes a wrong record)</td><td>0 min (oplog replay)</td><td>30 min</td><td>Atlas point-in-time → 1 min</td></tr>
          <tr><td>Single-collection corruption (logic bug wipes a table)</td><td>≤ 6 hours</td><td>2 hours</td><td>Atlas snapshot restore → 1 hour</td></tr>
          <tr><td>Whole-cluster loss (region outage)</td><td>≤ 6 hours</td><td>4 hours</td><td>Cross-region snapshot promote → 2 hour</td></tr>
          <tr><td>Total catastrophic loss (Atlas data loss)</td><td>≤ 7 days</td><td>1 day</td><td>Cross-region copy + offsite weekly export</td></tr>
        </tbody>
      </table>

      <h2>Restoration drill</h2>
      <p>We run a <strong>quarterly</strong> restoration drill (next: 2026-05-18):</p>
      <ol>
        <li>Pick a random tenant DB (excluding production-customer DBs)</li>
        <li>Trigger Atlas Cloud Backup restore to a fresh staging cluster</li>
        <li>Verify document count matches snapshot; spot-check 10 random documents for content equality</li>
        <li>Verify time-to-restore vs RTO target; file ticket if missed</li>
        <li>Tear down staging cluster</li>
      </ol>
      <p>Drill results filed in <code>backup_drill_results</code> collection + summarized in the quarterly Risk Assessment review.</p>

      <h2>Operator emergency runbook</h2>
      <h3>Step 1 — identify the loss</h3>
      <ul>
        <li>Was a single record deleted? → use oplog replay (1 min)</li>
        <li>Was a collection emptied? → use point-in-time recovery (1 hour)</li>
        <li>Is the whole tenant gone? → use cross-region snapshot promotion (2 hour)</li>
      </ul>

      <h3>Step 2 — restore</h3>
      <ol>
        <li>In Atlas UI, navigate to Backup → Restore → select snapshot &lt; loss event time</li>
        <li>Target: staging cluster first (NEVER restore directly over production)</li>
        <li>Once staging verifies, restore over production via cluster swap</li>
        <li>Notify affected tenants within 1 hour of any data loss event (per Incident Response playbook)</li>
      </ol>

      <h3>Step 3 — post-mortem</h3>
      <ul>
        <li>Document the loss + recovery in <a href="/legal/incident-response">Incident Response</a> registry within 24 hours</li>
        <li>Update Risk Assessment if a new failure mode discovered</li>
        <li>Customer comms: status page banner + email to affected tenants</li>
      </ul>

      <h2>Sign-off</h2>
      <p>Runbook approved Feb 18, 2026. Next review: May 18, 2026 (post Q1 drill).</p>
    </LegalDoc>
  );
}
